import { useEffect, useState, useRef } from 'react';
import api from '../lib/api';
import { Plus, ExternalLink, Download, Upload, FolderOpen, Link2, FileText, Search, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import { fmtDate, RESOURCE_CATEGORIES } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const CATEGORIES = RESOURCE_CATEGORIES;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResourcesPage() {
  const { isAdmin } = useAuth();
  const [resources, setResources] = useState([]);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const [openLink, setOpenLink] = useState(false);
  const [openFile, setOpenFile] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [fileBusy, setFileBusy] = useState('');
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);

  const fetchFileBlob = async (r) => {
    const token = localStorage.getItem('aawta_token');
    const resp = await fetch(`${BACKEND_URL}${r.file_path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!resp.ok) throw new Error(`File could not be loaded (HTTP ${resp.status})`);
    return resp.blob();
  };

  const openResource = async (r) => {
    if (r.resource_type !== 'file') return window.open(r.url, '_blank', 'noopener');
    setFileBusy(`open-${r.id}`);
    try {
      const blob = await fetchFileBlob(r);
      const type = blob.type || r.content_type || 'application/octet-stream';
      const typed = blob.type ? blob : new Blob([blob], { type });
      const previewable = type.startsWith('image/') || type === 'application/pdf' || type.startsWith('text/');
      if (!previewable) {
        const url = URL.createObjectURL(typed);
        const a = document.createElement('a');
        a.href = url;
        a.download = r.file_name || r.name || 'resource';
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        toast.info('Preview not available for this file type — downloading instead');
        return;
      }
      setPreview({ url: URL.createObjectURL(typed), type, name: r.name || r.file_name, resource: r });
    } catch (e) {
      toast.error(e.message || 'Could not open file');
    } finally { setFileBusy(''); }
  };

  const downloadResource = async (r) => {
    setFileBusy(`dl-${r.id}`);
    try {
      const blob = await fetchFileBlob(r);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = r.file_name || r.name || 'resource';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('Download started');
    } catch (e) {
      toast.error(e.message || 'Download failed');
    } finally { setFileBusy(''); }
  };

  const [link, setLink] = useState({ name: '', description: '', category: 'Partnership Sales Resources', url: '', resource_type: 'link', priority: 'medium', review_frequency: 'quarterly', status: 'active' });
  const [fileMeta, setFileMeta] = useState({ name: '', description: '', category: 'Contracts & Agreements' });

  const load = () => api.get('/resources').then((r) => setResources(r.data));
  useEffect(() => { load(); }, []);

  const filtered = resources.filter((r) => {
    if (cat !== 'all' && r.category !== cat) return false;
    if (q) return (r.name + ' ' + (r.description || '')).toLowerCase().includes(q.toLowerCase());
    return true;
  });

  const createLink = async () => {
    if (!link.name || !link.url) return toast.error('Name and URL required');
    await api.post('/resources', link);
    toast.success('Resource added');
    setOpenLink(false);
    setLink({ ...link, name: '', description: '', url: '' });
    load();
  };

  const uploadFile = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error('Select a file');
    const form = new FormData();
    form.append('file', file);
    form.append('name', fileMeta.name || file.name);
    form.append('description', fileMeta.description);
    form.append('category', fileMeta.category);
    try {
      await api.post('/resources/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('File uploaded');
      setOpenFile(false);
      setFileMeta({ name: '', description: '', category: 'Contracts & Agreements' });
      load();
    } catch (e) {
      toast.error('Upload failed');
    }
  };

  const remove = async (id) => {
    if (!confirm('Delete this resource?')) return;
    await api.delete(`/resources/${id}`);
    toast.success('Deleted');
    load();
  };

  return (
    <div className="space-y-6" data-testid="resources-page">
      <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <div className="flex gap-2 flex-1 max-w-xl">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search resources..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="resources-search" />
          </div>
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Dialog open={openLink} onOpenChange={setOpenLink}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-link-button"><Link2 className="w-4 h-4 mr-2" />Add Link</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add External Resource</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs">Name *</Label><Input value={link.name} onChange={(e) => setLink({ ...link, name: e.target.value })} data-testid="link-name" /></div>
                <div><Label className="text-xs">URL *</Label><Input value={link.url} onChange={(e) => setLink({ ...link, url: e.target.value })} data-testid="link-url" /></div>
                <div><Label className="text-xs">Description</Label><Textarea value={link.description} onChange={(e) => setLink({ ...link, description: e.target.value })} rows={2} /></div>
                <div>
                  <Label className="text-xs">Category</Label>
                  <Select value={link.category} onValueChange={(v) => setLink({ ...link, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={createLink} className="bg-magenta hover:bg-magenta/90" data-testid="save-link">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={openFile} onOpenChange={setOpenFile}>
            <DialogTrigger asChild>
              <Button className="bg-magenta hover:bg-magenta/90" data-testid="upload-file-button"><Upload className="w-4 h-4 mr-2" />Upload File</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Upload Internal File</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs">File</Label><Input type="file" ref={fileRef} data-testid="file-input" /></div>
                <div><Label className="text-xs">Display Name</Label><Input value={fileMeta.name} onChange={(e) => setFileMeta({ ...fileMeta, name: e.target.value })} placeholder="Optional override" /></div>
                <div><Label className="text-xs">Description</Label><Textarea value={fileMeta.description} onChange={(e) => setFileMeta({ ...fileMeta, description: e.target.value })} rows={2} /></div>
                <div>
                  <Label className="text-xs">Category</Label>
                  <Select value={fileMeta.category} onValueChange={(v) => setFileMeta({ ...fileMeta, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={uploadFile} className="bg-magenta hover:bg-magenta/90" data-testid="save-upload">Upload</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 items-stretch">
        {filtered.map((r) => {
          const isExpanded = !!expanded[r.id];
          const longDesc = (r.description || '').length > 110;
          const longTitle = (r.name || '').length > 55;
          return (
            <div key={r.id} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:border-magenta hover:shadow-md transition group flex flex-col h-full" data-testid={`resource-${r.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="w-11 h-11 rounded-xl bg-magenta/10 text-magenta flex items-center justify-center shrink-0">
                  {r.resource_type === 'file' ? <FileText className="w-5 h-5" /> : <Link2 className="w-5 h-5" />}
                </div>
                <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 text-right ml-2">{r.category}</span>
              </div>
              <div className={`font-heading font-bold text-navy text-base leading-snug mb-1 break-words ${isExpanded ? '' : 'line-clamp-2'}`} title={r.name}>{r.name}</div>
              <div className={isExpanded ? '' : ''}>
                <p className={`text-xs text-slate-500 whitespace-pre-wrap break-words ${isExpanded ? '' : 'line-clamp-2'}`} data-testid={`resource-desc-${r.id}`}>{r.description || '—'}</p>
                {(longDesc || longTitle) && (
                  <button
                    type="button"
                    onClick={() => setExpanded({ ...expanded, [r.id]: !isExpanded })}
                    className="text-[11px] font-semibold text-magenta hover:underline mt-1"
                    data-testid={`toggle-desc-${r.id}`}
                  >
                    {isExpanded ? 'Show Less' : 'Read More'}
                  </button>
                )}
              </div>
              <div className="text-[11px] text-slate-400 mt-3 mb-4">Updated {fmtDate(r.updated_at)} · v{r.version}</div>
              <div className="flex gap-2 mt-auto">
                <button
                  type="button"
                  onClick={() => openResource(r)}
                  disabled={fileBusy === `open-${r.id}`}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 bg-navy text-white hover:bg-navy/90 rounded-lg px-3 py-2 text-xs font-semibold transition disabled:opacity-60"
                  data-testid={`open-${r.id}`}
                >
                  {fileBusy === `open-${r.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />} Open
                </button>
                {r.resource_type === 'file' && (
                  <button
                    type="button"
                    onClick={() => downloadResource(r)}
                    disabled={fileBusy === `dl-${r.id}`}
                    className="inline-flex items-center justify-center bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg px-3 py-2 text-xs font-semibold transition disabled:opacity-60"
                    data-testid={`download-${r.id}`}
                    title={`Download ${r.file_name || ''}`}
                  >
                    {fileBusy === `dl-${r.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                  </button>
                )}
                {isAdmin && (
                  <button
                    onClick={() => remove(r.id)}
                    className="inline-flex items-center justify-center bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-lg px-3 py-2 text-xs font-semibold transition"
                    data-testid={`delete-${r.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-16 text-slate-500">
            <FolderOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            No resources yet
          </div>
        )}
      </div>

      {preview && (
        <Dialog open onOpenChange={(v) => { if (!v) { URL.revokeObjectURL(preview.url); setPreview(null); } }}>
          <DialogContent className="max-w-4xl w-[92vw] h-[85vh] flex flex-col" data-testid="resource-preview-dialog">
            <DialogHeader>
              <DialogTitle className="truncate pr-8" title={preview.name}>{preview.name}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 min-h-0 bg-slate-100 rounded-lg overflow-hidden flex items-center justify-center">
              {preview.type.startsWith('image/') ? (
                <img src={preview.url} alt={preview.name} className="max-w-full max-h-full object-contain" data-testid="preview-image" />
              ) : (
                <iframe title="Document preview" src={preview.url} className="w-full h-full border-0" data-testid="preview-frame" />
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2 shrink-0">
              <Button variant="outline" size="sm" onClick={() => window.open(preview.url, '_blank')} data-testid="preview-open-tab">
                <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open in New Tab
              </Button>
              <Button size="sm" className="bg-magenta hover:bg-magenta/90" onClick={() => downloadResource(preview.resource)} data-testid="preview-download">
                <Download className="w-3.5 h-3.5 mr-1.5" /> Download
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
